import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Radio, Button, message, Spin, Statistic, Row, Col, Alert, Empty, Space
} from 'antd';
import {
  EditOutlined, CheckCircleOutlined, ReloadOutlined, DownloadOutlined,
} from '@ant-design/icons';
import html2canvas from 'html2canvas';
import { SurveyRecord, DIMENSION_COLORS } from '../types';
import HappinessWheel from '../components/HappinessWheel';
import ScoreBreakdown from '../components/ScoreBreakdown';
import * as api from '../api';

const SurveyPage: React.FC = () => {
  const [questions, setQuestions] = useState<any[]>([]);
  const [dimensions, setDimensions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<number[]>(Array(18).fill(0));
  const [latest, setLatest] = useState<SurveyRecord | null>(null);
  const [allSurveys, setAllSurveys] = useState<SurveyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [existsToday, setExistsToday] = useState(false);

  const wheelRef = useRef<HTMLDivElement>(null);

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [qs, dims, sv, lt] = await Promise.all([
        api.getQuestions(),
        api.getDimensions(),
        api.getSurveys(),
        api.getLatestSurvey(),
      ]);
      setQuestions(qs);
      setDimensions(dims);
      setAllSurveys(sv);
      setLatest(lt);

      if (sv.some(s => s.surveyDate === today)) {
        setExistsToday(true);
      }
    } catch (err: any) {
      message.error('加载数据失败: ' + (err?.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionIndex: number, value: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = value;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    if (answers.some(a => a === 0)) {
      message.warning('请完成所有18道题的评分');
      return;
    }

    setSubmitting(true);
    try {
      const record = await api.createSurvey({ surveyDate: today, answers, notes: '' });
      message.success('问卷提交成功！幸福之轮已生成');
      setLatest(record);
      setExistsToday(true);
      setAllSurveys(prev => [record, ...prev]);
      // 手机端提交后自动滚动到轮图
      setTimeout(() => {
        wheelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } catch (err: any) {
      message.error(err?.response?.data?.message || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async () => {
    const el = document.querySelector('.wheel-export-area') as HTMLElement;
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { backgroundColor: '#ffffff', scale: 2 });
      const link = document.createElement('a');
      link.download = `幸福之轮_${latest?.surveyDate || today}.png`;
      link.href = canvas.toDataURL();
      link.click();
      message.success('图片已导出');
    } catch {
      message.error('导出失败');
    }
  };

  const dimCodes = ['physical', 'mental', 'relationship', 'career', 'wealth', 'meaning'];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div className="survey-page">
      <Row gutter={[24, 24]}>
        {/* Survey column — full width on mobile */}
        <Col xs={24} lg={14}>
          <Card
            className="survey-card"
            title={<><EditOutlined /> LES 生命质量现状测评</>}
            extra={
              <Button icon={<ReloadOutlined />} size="small" onClick={loadData}>
                刷新
              </Button>
            }
          >
            {existsToday && (
              <Alert
                message="今天已填写过问卷"
                description={`${today} 已有一份问卷记录，每人每天只能填写一次。`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <div className="survey-rating-hint">
              评分标准：1=完全不符合 · 3=部分符合 · 5=完全符合
              <br />
              请根据最近一个月的真实感受评分（2分和4分为中间值）。
            </div>

            {questions.length === 0 ? (
              <Empty description="暂无问卷题目" />
            ) : (
              dimCodes.map(dimCode => {
                const dim = dimensions.find(d => d.code === dimCode);
                const dimQs = questions.filter(q => q.dimension === dimCode);
                const dimScore = dimQs.reduce((sum, q) => {
                  const idx = questions.indexOf(q);
                  return sum + (answers[idx] || 0);
                }, 0);
                const color = DIMENSION_COLORS[dimCode] || '#999';

                return (
                  <Card
                    key={dimCode}
                    size="small"
                    className="dimension-card"
                    title={
                      <div className="dimension-title">
                        <span className="dimension-name" style={{ color }}>
                          {dim?.name_cn || dimCode}
                        </span>
                        <span className="dimension-score">
                          当前: {dimScore}/15分
                        </span>
                      </div>
                    }
                    style={{ borderLeft: `4px solid ${color}` }}
                  >
                    {dimQs.map((q, qi) => {
                      const idx = questions.indexOf(q);
                      return (
                        <div key={q.id} className={`question-item ${qi < dimQs.length - 1 ? 'question-item-bordered' : ''}`}>
                          <div className="question-text">
                            <span className="question-number" style={{ color }}>Q{q.order}.</span>
                            {q.text}
                          </div>
                          <Radio.Group
                            className="question-rating"
                            value={answers[idx]}
                            onChange={e => handleAnswerChange(idx, e.target.value)}
                            size="small"
                            disabled={existsToday}
                          >
                            {[1, 2, 3, 4, 5].map(v => (
                              <Radio.Button key={v} value={v}>{v}</Radio.Button>
                            ))}
                          </Radio.Group>
                        </div>
                      );
                    })}
                  </Card>
                );
              })
            )}

            <div className="survey-submit-row">
              <Space className="survey-submit-inner" direction="vertical" size={12} style={{ width: '100%' }}>
                <div style={{ fontSize: 13, color: '#999', textAlign: 'center' }}>
                  填写日期：{today}
                </div>
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<CheckCircleOutlined />}
                  onClick={handleSubmit}
                  loading={submitting}
                  disabled={existsToday}
                  className="survey-submit-btn"
                >
                  提交问卷 → 生成幸福之轮
                </Button>
              </Space>
            </div>
          </Card>
        </Col>

        {/* Wheel column — on mobile this comes after and scrolls into view */}
        <Col xs={24} lg={10} ref={wheelRef}>
          <div className="wheel-export-area">
            <Card
              className="wheel-card"
              title={<span style={{ color: '#2ECC71' }}>你的幸福之轮</span>}
            >
              {latest ? (
                <>
                  <Statistic
                    title="综合总分"
                    value={latest.totalScore}
                    suffix="/ 90"
                    valueStyle={{ color: '#2ECC71', fontSize: 36, fontWeight: 'bold' }}
                  />
                  <div className="wheel-chart-container">
                    <HappinessWheel records={[latest]} />
                  </div>
                  <ScoreBreakdown record={latest} />
                  <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <Button icon={<DownloadOutlined />} onClick={handleExport} block={window.innerWidth < 768}>
                      导出为图片
                    </Button>
                  </div>
                </>
              ) : (
                <div className="wheel-empty">
                  <Empty description="填写问卷后点击提交，即可看到你的幸福之轮" />
                </div>
              )}
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SurveyPage;
